import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { formatDate } from '../../../../utils/luxon';
import { ArkOpinion } from '../../my-opinions/store';
import { MetaField } from '../fields/MetaField';
import { SingleField } from '../fields/SingleField';
import { getOpenFieldsAndSections, getPreviousOpinionDate, toggleOpenFieldSection } from '../store';
import { General, UpdatedOpinionField } from '../store/types';
import styles from './OpinionSection.module.scss';
import { SectionWrapper } from './SectionWrapper';

interface GeneralSectionProps {
    opinion: ArkOpinion;
    general: General;
    previousGeneral: General | null;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedOpinionField[];
}

export const GeneralSection: React.FC<GeneralSectionProps> = ({ opinion, general, isEditing, isUpdating, fieldsUpdated, previousGeneral }) => {
    const dispatch = useAppDispatch();
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const previousOpinionDate = useAppSelector(getPreviousOpinionDate);
    const getFieldOpen = useCallback((id: string) => openFieldsAndSections.includes(id), [openFieldsAndSections]);
    const toggleFieldOpen = useCallback((id: string) => dispatch(toggleOpenFieldSection(id)), [dispatch]);
    const sectionId = 'general';
    const { jurisdiction, dateOfOpinion, type, focus, subjectOfOpinion, background } = opinion;
    const { dateOfReview, reviewer, provider } = general;
    const previousDateOfOpinion = useMemo(() => !isNull(previousOpinionDate) && formatDate(previousOpinionDate) || null, [previousOpinionDate]);

    const getPreviousField = useCallback((fieldId: keyof General) => !isNull(previousGeneral) && previousGeneral[fieldId] || null, [previousGeneral]);

    return (
        <SectionWrapper id={sectionId} label='General' isEditing={isEditing} isUpdating={isUpdating}>
            <div className={styles.generalSectionWrapper}>
                <div className={styles.sectionGroup}>
                    <MetaField id='jurisdiction' label='Jurisdiction' value={[jurisdiction!]} width='calc(50% - 10px)' />
                    <MetaField id='focus' label='Focus' value={[focus]} width='calc(50% - 10px)' />
                </div>
                <MetaField id='date-of-opinion' label='Date of Opinion' value={[formatDate(dateOfOpinion!)]} previousValue={previousDateOfOpinion} width='100%' />
                <div className={styles.sectionGroup}>
                    <MetaField id='type' label='Type' value={[type!]} width='calc(50% - 10px)' />
                    <SingleField
                        field={dateOfReview}
                        previousField={getPreviousField('dateOfReview')}
                        fieldId='dateOfReview'
                        sectionId={sectionId}
                        isEditing={isEditing}
                        isUpdating={isUpdating}
                        fieldsUpdated={fieldsUpdated}
                        getFieldOpen={getFieldOpen}
                        toggleFieldOpen={toggleFieldOpen}
                        width='calc(50% - 10px)'
                    />
                </div>
                <div className={styles.sectionGroup}>
                    <SingleField
                        field={reviewer}
                        previousField={getPreviousField('reviewer')}
                        fieldId='reviewer'
                        sectionId={sectionId}
                        isEditing={isEditing}
                        isUpdating={isUpdating}
                        fieldsUpdated={fieldsUpdated}
                        getFieldOpen={getFieldOpen}
                        toggleFieldOpen={toggleFieldOpen}
                        width='calc(50% - 10px)'
                    />
                    <SingleField
                        field={provider}
                        previousField={getPreviousField('provider')}
                        fieldId='provider'
                        sectionId={sectionId}
                        isEditing={isEditing}
                        isUpdating={isUpdating}
                        fieldsUpdated={fieldsUpdated}
                        getFieldOpen={getFieldOpen}
                        toggleFieldOpen={toggleFieldOpen}
                        width='calc(50% - 10px)'
                    />
                </div>
                {!isNull(subjectOfOpinion) ? <MetaField id='subject-of-opinion' label='Subject of Opinion' value={[subjectOfOpinion]} width='calc(100% - 10px)' maxWidth='800' /> : null}
                {!isNull(background) ? <MetaField id='background' label='Background' value={[background]} width='calc(100% - 10px)' maxWidth='800' /> : null}
            </div>
        </SectionWrapper>
    );
};
